import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const instalacionesPhotos = [
  {
    src: 'https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/d26a0994-7b40-4d91-97c3-bbc184c40b97_Casa-matriz-Ingemetalsur-2.jpg?v=2943615cdfae793fe27929cda9b9f4c8',
    alt: 'Casa Matriz IngemetalSur - Área de trabajo y operaciones',
    title: 'Área de Operaciones',
    desc: 'Espacio de trabajo donde coordinamos cada proyecto en Chile.',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/bb261f02-579f-4668-9b0f-860f63c2a3f0_Casa-matriz-Ingemetalsur-4.jpg?v=b1036c560cf9aed63ae2aae75f7df189',
    alt: 'Casa Matriz IngemetalSur - Oficinas y administración',
    title: 'Oficinas Administrativas',
    desc: 'Desde aquí gestionamos presupuestos, planificación y atención al cliente.',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/0b4b73d2-6a55-4b58-b11a-7a73b0a1c6ca_Casa-matriz-Ingemetalsur-1.jpg?v=954d3a28a00f08032853341299ad9ce1',
    alt: 'Casa Matriz IngemetalSur - Fachada principal en Temuco',
    title: 'Fachada Principal',
    desc: 'Nuestra Casa Matriz desde la entrada principal, ubicada en Temuco.',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/c8384976-0e72-4bdb-9022-427dd8447e5a_Casa-matriz-Ingemetalsur-3.jpg?v=77481834bf5d00a1d44324dd6b52ce6f',
    alt: 'Casa Matriz IngemetalSur - Taller y bodega',
    title: 'Taller y Bodega',
    desc: 'Instalaciones para preparación de materiales y logística de obras.',
  },
];

const instalacionesVideos = [
  {
    src: 'https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/76bde031-6dcb-498e-bf8c-e0c0ba715363_20240515_101924_054300.mp4?v=f023f4efd2f9864cc14ab69796de2f27',
    poster: 'https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/0b4b73d2-6a55-4b58-b11a-7a73b0a1c6ca_Casa-matriz-Ingemetalsur-1.jpg?v=954d3a28a00f08032853341299ad9ce1',
    title: 'Recorrido Instalaciones 2024',
    desc: 'Video recorrido por nuestras instalaciones principales en Temuco.',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/76ec465e-4fae-46ee-a410-2706f39d4360_20240531_122511_053539.mp4?v=193a32680a5deab8176c2ec302444046',
    poster: 'https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/d26a0994-7b40-4d91-97c3-bbc184c40b97_Casa-matriz-Ingemetalsur-2.jpg?v=2943615cdfae793fe27929cda9b9f4c8',
    title: 'Operaciones IngemetalSur',
    desc: 'Vista de nuestras operaciones y logística interna en Casa Matriz.',
  },
];

const reveal = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal ${r.isVisible ? 'reveal-visible' : ''}`;
const revealLeft = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-left ${r.isVisible ? 'reveal-visible' : ''}`;
const revealRight = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-right ${r.isVisible ? 'reveal-visible' : ''}`;
const revealScale = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-scale ${r.isVisible ? 'reveal-visible' : ''}`;

const InstalacionesPage = () => {
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    if (lightbox !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightbox]);

  const heroR = useScrollAnimation();
  const infoLeftR = useScrollAnimation();
  const infoRightR = useScrollAnimation();
  const infoGridR = useScrollAnimation();
  const casaMatrizR = useScrollAnimation();
  const videosR = useScrollAnimation();
  const ubicacionR = useScrollAnimation();

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/edited_1c5bfda2a72d9d9e62cc6cca08cbacb9_877ddadb.jpg"
              alt="Planta industrial de fabricación de estructuras metálicas - Instalaciones IngemetalSur"
              className="w-full h-full object-cover object-center opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0C1D2E]/70 via-[#0C1D2E]/50 to-[#0C1D2E]/20"></div>
          </div>
          <div ref={heroR.ref} className={`relative z-10 max-w-7xl mx-auto px-4 md:px-8 ${reveal(heroR)}`}>
            <div className="max-w-2xl">
              <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Conócenos</span>
              <h1 className="text-4xl md:text-5xl font-black text-white mt-2 mb-4 leading-tight">
                Nuestras<br />
                <span className="text-[#C88A2A]">Instalaciones</span>
              </h1>
              <p className="text-slate-200 text-lg leading-relaxed">
                Nuestra Casa Matriz en Temuco, desde donde operamos y coordinamos cada proyecto en todo Chile.
              </p>
            </div>
          </div>
        </section>

        {/* Info */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div ref={infoLeftR.ref} className={`${revealLeft(infoLeftR)}`}>
                <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Casa Matriz</span>
                <h2 className="text-3xl md:text-4xl font-black text-[#0C1D2E] mt-2 mb-6 leading-tight">
                  Operamos desde Temuco<br />
                  <span className="text-[#C88A2A]">para todo Chile</span>
                </h2>
                <p className="text-[#475569] text-base leading-relaxed mb-4">
                  Nuestras instalaciones en Temuco son el centro neurálgico de IngemetalSur. Desde aquí planificamos, coordinamos y ejecutamos cada proyecto que llega a distintas regiones del país.
                </p>
                <p className="text-[#475569] text-base leading-relaxed">
                  Contamos con taller, bodega, oficinas administrativas y áreas logísticas que nos permiten responder con rapidez a las necesidades de nuestros clientes desde Arica hasta Punta Arenas.
                </p>
                <div ref={infoGridR.ref} className={`grid grid-cols-2 gap-4 mt-8 ${reveal(infoGridR)}`}>
                  {[
                    { icon: 'ri-map-pin-line', label: 'Temuco, Araucanía', desc: 'Ubicación central' },
                    { icon: 'ri-building-line', label: 'Instalaciones propias', desc: 'Taller + oficinas' },
                    { icon: 'ri-truck-line', label: 'Logística nacional', desc: 'Despacho a todo Chile' },
                    { icon: 'ri-team-line', label: 'Equipo local', desc: 'Personal calificado' },
                  ].map((item, i) => (
                    <div
                      key={item.label}
                      className={`bg-[#F8FAFC] rounded-lg p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 reveal ${infoGridR.isVisible ? 'reveal-visible' : ''}`}
                      style={{ transitionDelay: `${i * 100}ms` }}
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-[#1A3A5C]/10 rounded-md mb-2">
                        <i className={`${item.icon} text-[#C88A2A] text-base`}></i>
                      </div>
                      <div className="font-bold text-[#0C1D2E] text-sm">{item.label}</div>
                      <div className="text-[#64748B] text-xs mt-0.5">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div ref={infoRightR.ref} className={`relative ${revealRight(infoRightR)}`}>
                <img
                  src="https://storage.readdy-site.link/project_files/e9e9255d-4fa4-42a2-878c-87b7e92a4901/d26a0994-7b40-4d91-97c3-bbc184c40b97_Casa-matriz-Ingemetalsur-2.jpg?v=2943615cdfae793fe27929cda9b9f4c8"
                  alt="Interior Casa Matriz IngemetalSur"
                  className="w-full h-96 object-cover object-top rounded-lg"
                />
                <div className="absolute -bottom-4 -left-4 bg-[#1A3A5C] text-white p-4 rounded-lg">
                  <div className="text-3xl font-black">+25</div>
                  <div className="text-white/70 text-xs mt-0.5">Años de experiencia</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Casa Matriz — Galería Grid prominente */}
        <section className="py-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={casaMatrizR.ref} className={`text-center mb-14 ${reveal(casaMatrizR)}`}>
              <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Casa Matriz</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#0C1D2E] mt-3">
                Conoce nuestras instalaciones
              </h2>
              <p className="text-[#475569] text-base max-w-lg mx-auto mt-3">
                Recorrido visual por la Casa Matriz de IngemetalSur en Temuco, donde planificamos y coordinamos cada proyecto.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {instalacionesPhotos.map((photo, i) => (
                <div
                  key={photo.alt}
                  className={`group rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer hover:-translate-y-1 reveal ${casaMatrizR.isVisible ? 'reveal-visible' : ''}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                  onClick={() => setLightbox(i)}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <span className="text-white text-sm font-semibold flex items-center gap-2">
                        <i className="ri-zoom-in-line"></i>
                        Ver ampliado
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-[#0C1D2E] text-base mb-1">{photo.title}</h3>
                    <p className="text-[#475569] text-sm">{photo.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Videos */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={videosR.ref} className={`flex items-end justify-between mb-10 ${reveal(videosR)}`}>
              <div>
                <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Videos</span>
                <h2 className="text-3xl md:text-4xl font-black text-[#0C1D2E] mt-2">Conoce nuestras instalaciones</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {instalacionesVideos.map((video, i) => (
                <div
                  key={i}
                  className={`rounded-xl overflow-hidden bg-[#0C1D2E] shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 reveal ${videosR.isVisible ? 'reveal-visible' : ''}`}
                  style={{ transitionDelay: `${i * 150}ms` }}
                >
                  <div className="relative aspect-video">
                    <video
                      src={video.src}
                      poster={video.poster}
                      controls
                      preload="metadata"
                      className="w-full h-full object-cover"
                      aria-label={video.title}
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-white text-base mb-1">{video.title}</h3>
                    <p className="text-white/60 text-sm">{video.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ubicación */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={ubicacionR.ref} className={`text-center mb-10 ${reveal(ubicacionR)}`}>
              <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Ubicación</span>
              <h2 className="text-3xl md:text-4xl font-black text-[#0C1D2E] mt-2 mb-4">¿Dónde encontrarnos?</h2>
              <p className="text-[#475569] text-base max-w-xl mx-auto">
                Parcela 18 camino vecinal sector El Retiro, Temuco, Región de La Araucanía, Chile.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden h-80 md:h-96 bg-[#F8FAFC]">
              <iframe
                title="Ubicación IngemetalSur Temuco"
                src="https://maps.google.com/maps?q=-38.701185,-72.534385&z=15&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a
                href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20visitar%20sus%20instalaciones"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white font-bold px-7 py-3.5 rounded-md transition-colors duration-200 whitespace-nowrap hover:scale-[1.02] active:scale-[0.98]"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Agendar visita
              </a>
              <a
                href="mailto:contacto@ingemetalsur.cl"
                className="inline-flex items-center justify-center gap-2 bg-[#1A3A5C] hover:bg-[#152e45] text-white font-bold px-7 py-3.5 rounded-md transition-colors duration-200 whitespace-nowrap hover:scale-[1.02] active:scale-[0.98]"
              >
                <i className="ri-mail-line text-lg"></i>
                contacto@ingemetalsur.cl
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors cursor-pointer"
            onClick={() => setLightbox(null)}
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          {lightbox > 0 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }}
              aria-label="Anterior"
            >
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
          )}
          {lightbox < instalacionesPhotos.length - 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }}
              aria-label="Siguiente"
            >
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
          )}
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={instalacionesPhotos[lightbox].src}
              alt={instalacionesPhotos[lightbox].alt}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <p className="text-white font-semibold text-base">{instalacionesPhotos[lightbox].title}</p>
              <p className="text-white/60 text-sm">{instalacionesPhotos[lightbox].desc}</p>
              <p className="text-white/40 text-xs mt-2">{lightbox + 1} / {instalacionesPhotos.length}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default InstalacionesPage;